import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MAX_ADDITIONAL_ESCROW, MAX_ADDITIONAL_PRINCIPAL } from '@ld.consumer-portal/constants';
import type { LoanResponse } from '@ld.consumer-portal/models';
import { EnvironmentFeatureService } from './environment-feature.service';

export enum PaymentLoanStatus {
  OnTime = 0,
  OneMonthPaidOff = 1,
  TwoMonthsPaidOff = 2,
  PastDue = 3,
  Delinquent = 4,
  DelinquentPlus = 5,
  ActiveForbearance = 6,
  ActiveRepayment = 7,
  ActiveTrial = 8,
}

export interface PaymentEligibilityResult {
  isEligible: boolean;
  errors: string[];
  loanStatus: PaymentLoanStatus;
  maxAdditionalPrincipal: number;
  maxAdditionalEscrow: number;
  isPaymentInSuspense: boolean;
  delinquentPaymentCount: number;
}

export const STOP_CODE_BLOCKED = {
  badCheck: ['1', '2', '3'], // Any non-zero value blocks
  pif: '1', // Paid in full
  foreclosure: 'F', // In foreclosure
  process: ['F', 'L', 'N', 'R'], // Foreclosure, Loss Mitigation, NOI, REO
};

export const PROCESS_STOP_CODES = {
  ALLOWED: ['0', '9', 'T'],
  BLOCKED_FORECLOSURE: ['F'],
  BLOCKED_TRANSFER: ['G'],
  BLOCKED_OTHER: ['B', 'C', 'L', 'N', 'R'],
} as const;

@Injectable({
  providedIn: 'root',
})
export class PaymentEligibilityService {
  private readonly translocoService = inject(TranslocoService);
  private readonly envFeatureService = inject(EnvironmentFeatureService);
  checkEligibility(loanResponse: LoanResponse, options?: { isHeloc?: boolean }): PaymentEligibilityResult {
    const loan = loanResponse.loan;
    const additionalInfo = loanResponse.loanAdditionalInfo;
    const eligibility = loanResponse.oneTimePaymentEligibility;
    const mitigationInfo = loan.mitigationInfo;
    const errors: string[] = [];

    if (mitigationInfo?.isSystemChangesInProgress) {
      errors.push(this.translocoService.translate('constantsPaymentSystemChangesInProgress'));
      return this.buildResult(loan, additionalInfo, errors);
    }

    if (this.envFeatureService.isEnabled('DefaultPayment') && this.hasActivePlan(loan)) {
      return this.buildResult(loan, additionalInfo, errors);
    }

    if (!options?.isHeloc && eligibility?.isEligibile !== 'Allowed') {
      const stopCodeErrors = this.checkStopCodes(loan);
      errors.push(...stopCodeErrors);
    }

    if (eligibility?.isEligibile !== 'Allowed' && eligibility?.isEligibile !== 'true') {
      const apiErrors = eligibility?.errors ?? [];
      const errorMessages = apiErrors.map((err) =>
        typeof err === 'string' ? err : (err.errorMessage ?? err.message ?? 'Unknown error'),
      );
      errors.push(...errorMessages);
    }

    return this.buildResult(loan, additionalInfo, errors);
  }

  private buildResult(
    loan: LoanResponse['loan'],
    additionalInfo: LoanResponse['loanAdditionalInfo'],
    errors: string[],
  ): PaymentEligibilityResult {
    const maxAdditionalPrincipal = MAX_ADDITIONAL_PRINCIPAL;
    const maxAdditionalEscrow = MAX_ADDITIONAL_ESCROW;

    return {
      isEligible: errors.length === 0,
      errors,
      loanStatus: this.determineLoanStatus(loan),
      maxAdditionalPrincipal,
      maxAdditionalEscrow,
      isPaymentInSuspense: loan.isPaymentInSuspense ?? false,
      delinquentPaymentCount: loan.delinquentPaymentCount ?? 0,
    };
  }

  determineLoanStatus(loan: LoanResponse['loan']): PaymentLoanStatus {
    const mitigationInfo = loan.mitigationInfo;

    if (mitigationInfo?.isActiveForbearancePlan) {
      return PaymentLoanStatus.ActiveForbearance;
    }
    if (mitigationInfo?.isActiveRepaymentPlan) {
      return PaymentLoanStatus.ActiveRepayment;
    }
    if (mitigationInfo?.isActiveTrialPlan) {
      return PaymentLoanStatus.ActiveTrial;
    }

    if ((loan.delinquentPaymentCount ?? 0) >= 5) {
      return PaymentLoanStatus.DelinquentPlus;
    }

    return (loan.loanStatus ?? 0) as PaymentLoanStatus;
  }

  private checkStopCodes(loan: LoanResponse['loan']): string[] {
    const errors: string[] = [];

    if (loan.badCheckStopCode && STOP_CODE_BLOCKED.badCheck.includes(loan.badCheckStopCode)) {
      errors.push(this.translocoService.translate('servicesErrorsPaymentBadCheckStopCode'));
    }

    if (loan.pifStopCode === STOP_CODE_BLOCKED.pif) {
      errors.push(this.translocoService.translate('servicesErrorsPaymentPaidInFull'));
    }

    if (loan.foreclosureStopCode === STOP_CODE_BLOCKED.foreclosure) {
      errors.push(this.translocoService.translate('servicesErrorsPaymentForeclosure'));
    }

    const processCodeResult = this.checkProcessStopCode(loan.processStopCode);
    if (processCodeResult.isBlocked && processCodeResult.message) {
      errors.push(processCodeResult.message);
    }

    return errors;
  }

  checkProcessStopCode(code: string | null | undefined): {
    isBlocked: boolean;
    message: string | null;
    showTransferWarning: boolean;
  } {
    if (!code || PROCESS_STOP_CODES.ALLOWED.includes(code as '0' | 'T')) {
      return { isBlocked: false, message: null, showTransferWarning: false };
    }

    if (PROCESS_STOP_CODES.BLOCKED_FORECLOSURE.includes(code as 'F')) {
      return {
        isBlocked: true,
        message: this.translocoService.translate('servicesErrorsPaymentForeclosureGeneric'),
        showTransferWarning: false,
      };
    }

    if (PROCESS_STOP_CODES.BLOCKED_TRANSFER.includes(code as 'G')) {
      return {
        isBlocked: true,
        message: this.translocoService.translate('constantsPaymentTransferInProgress'),
        showTransferWarning: true,
      };
    }

    return {
      isBlocked: true,
      message: this.translocoService.translate('servicesErrorsPaymentTemporarilyUnavailable'),
      showTransferWarning: false,
    };
  }

  getFormVariant(status: PaymentLoanStatus): 'ontime' | 'paid-off' | 'past-due' | 'delinquent' {
    switch (status) {
      case PaymentLoanStatus.OnTime:
        return 'ontime';
      case PaymentLoanStatus.OneMonthPaidOff:
      case PaymentLoanStatus.TwoMonthsPaidOff:
        return 'paid-off';
      case PaymentLoanStatus.PastDue:
        return 'past-due';
      case PaymentLoanStatus.Delinquent:
      case PaymentLoanStatus.DelinquentPlus:
      case PaymentLoanStatus.ActiveForbearance:
      case PaymentLoanStatus.ActiveRepayment:
      case PaymentLoanStatus.ActiveTrial:
        return 'delinquent';
      default:
        return 'ontime';
    }
  }

  isAdditionalPrincipalAllowed(status: PaymentLoanStatus): boolean {
    return status !== PaymentLoanStatus.TwoMonthsPaidOff;
  }

  isPrepayAvailable(status: PaymentLoanStatus): boolean {
    if (
      status === PaymentLoanStatus.ActiveForbearance ||
      status === PaymentLoanStatus.ActiveRepayment ||
      status === PaymentLoanStatus.ActiveTrial
    ) {
      return false;
    }
    return [
      PaymentLoanStatus.OnTime,
      PaymentLoanStatus.OneMonthPaidOff,
      PaymentLoanStatus.TwoMonthsPaidOff,
      PaymentLoanStatus.PastDue,
    ].includes(status);
  }

  validateActivePlanRestrictions(
    loan: LoanResponse['loan'],
    paymentSelection: {
      prepaySelected?: boolean;
      additionalPrincipal?: number;
      additionalEscrow?: number;
      totalAmount?: number;
    },
  ): string[] {
    const errors: string[] = [];
    const mitigationInfo = loan.mitigationInfo;

    if (mitigationInfo?.isActiveForbearancePlan) {
      if (paymentSelection.prepaySelected) {
        errors.push(this.translocoService.translate('servicesErrorsPaymentPrepayForbearance'));
      }
    }

    if (mitigationInfo?.isActiveRepaymentPlan) {
      if (paymentSelection.prepaySelected) {
        errors.push(this.translocoService.translate('servicesErrorsPaymentPrepayRepayment'));
      }
    }

    if (mitigationInfo?.isActiveTrialPlan) {
      if ((paymentSelection.additionalPrincipal ?? 0) > 0) {
        errors.push(this.translocoService.translate('paymentAmountPrincipalNotAvailable'));
      }
      if ((paymentSelection.additionalEscrow ?? 0) > 0) {
        errors.push(this.translocoService.translate('paymentAmountEscrowNotAvailable'));
      }
    }

    return errors;
  }

  hasActivePlan(loan: LoanResponse['loan']): boolean {
    const mi = loan.mitigationInfo;
    return !!(mi?.isActiveForbearancePlan || mi?.isActiveRepaymentPlan || mi?.isActiveTrialPlan);
  }
}
